import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingGuard } from '@/hooks/useOnboardingGuard';
import { useLicenseStatus } from '@/hooks/useLicenseStatus';
import { Loader2 } from 'lucide-react';

interface UserOnlyRouteProps {
  children: React.ReactNode;
}

/**
 * Determina a rota esperada baseado no estado do onboarding
 */
function getExpectedRoute(step: string | null, clientId: string | null): string {
  switch (step) {
    case "completed":
      return "/portal";
    case "plan":
    case "company":
      if (clientId) {
        return "/onboarding/plan";
      }
      return "/onboarding/company";
    case "personal":
      return "/onboarding/company";
    case "verified":
    case "email_sent":
    case "pending":
    default:
      return "/onboarding/personal";
  }
}

const UserOnlyRoute = ({ children }: UserOnlyRouteProps) => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { isLoading, onboardingStep, clientId, isEmailVerified } = useOnboardingGuard();
  const licenseStatus = useLicenseStatus(clientId);


  // Loading enquanto verifica auth ou onboarding
  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Redirecionar para login se não autenticado
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirecionar para verificação de email se não verificado
  if (!isEmailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Redirecionar para onboarding se não estiver completo
  if (onboardingStep !== "completed") {
    const expectedRoute = getExpectedRoute(onboardingStep, clientId);
    return <Navigate to={expectedRoute} replace />;
  }

  // Se onboarding completou mas ainda não temos clientId, aguardar
  // (caso raro de inconsistência de dados)
  if (!clientId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando dados...</p>
        </div>
      </div>
    );
  }

  // Aguardar carregamento do status da licença
  // IMPORTANTE: Só verificar licença quando temos clientId E não está loading
  if (licenseStatus.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando licença...</p>
        </div>
      </div>
    );
  }

  // Redirecionar para página de licença expirada se não válida
  if (!licenseStatus.isValid || licenseStatus.isExpired) {
    return <Navigate to="/license-expired" replace />;
  }

  return <>{children}</>;
};

export default UserOnlyRoute;
